import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CachedIcon from "@mui/icons-material/Cached";
import { Box, Button, IconButton, LinearProgress, Typography } from "@mui/material";
import { type FC, useEffect } from "react";
import { useMarketingActionFeedbackBannerStyles } from "./MarketingActionFeedbackBanner.styles";
import { useQueryUnseenBulkOperations } from "src/api/bulk-operations/query";
import { useMarkBulkOperationSeen } from "src/api/bulk-operations/mutation";
import type { BulkOperation } from "src/api/bulk-operations/type";
import type {
    BulkActionFeedbackStatus,
    ContextualBannerProps,
    MarketingActionFeedbackBannerContentProps,
    SingleBannerProps,
} from "./types";
import { ClearIcon } from "@mui/x-date-pickers";
import { useHistory } from "react-router";
import { useTranslation } from "src/design-system/core/TranslationContext";
import { useQueryString } from "src/design-system/core/hooks";
import {
    MARKETING_ACTION_FEEDBACK_BANNER_COMPONENTS_NAMESPACE,
    MARKETING_ACTION_FEEDBACK_BANNER_TK,
} from "./config";
import {
    getFeedbackStatus,
    getSubtitleText,
    getErrorButtonLabel,
    getErrorNavigationUrl,
    getEntityLabel,
    getProgressPercent,
    hasSpecificIds,
} from "src/components/MarketingActionFeedbackBanner/utils";
import {
    OFFER_LIST_FROM_MARKETING_FEEDBACK_QS_KEY,
    OFFER_LIST_OFFER_ID_QS_KEY,
    OFFER_LIST_VIEW_TAB_QS_KEY,
    OffersTablesTabsEnum,
} from "src/views/offer/routes";
import {
    SPONSORED_AD_LIST_FROM_MARKETING_FEEDBACK_QS_KEY,
    SPONSORED_AD_LIST_SPECIFIC_ID_QS_KEY,
    SPONSORED_ADS_TABLES_VIEW_QS_KEY,
    SponsoredAdsTablesTabsEnum,
} from "src/views/sponsored-ads/routes";
import { PATH_OFFER, PATH_SPONSORED_ADS } from "src/routesConstants";

const STATUS_ICON_MAP: Record<BulkActionFeedbackStatus, FC> = {
    processing: () => <CachedIcon color="info" />,
    success: () => <CheckCircleOutlineIcon color="success" />,
    partial: () => <WarningAmberIcon color="warning" />,
    error: () => <ErrorOutlineIcon color="error" />,
};

function useContextualBannerVisibility() {
    const history = useHistory();
    const [offerIds] = useQueryString(OFFER_LIST_OFFER_ID_QS_KEY);
    const [offerTab] = useQueryString(OFFER_LIST_VIEW_TAB_QS_KEY);
    const [offerFromMarketingFeedback] = useQueryString(OFFER_LIST_FROM_MARKETING_FEEDBACK_QS_KEY);
    const [sponsoredAdIds] = useQueryString(SPONSORED_AD_LIST_SPECIFIC_ID_QS_KEY);
    const [sponsoredAdsTab] = useQueryString(SPONSORED_ADS_TABLES_VIEW_QS_KEY);
    const [sponsoredAdFromMarketingFeedback] = useQueryString(
        SPONSORED_AD_LIST_FROM_MARKETING_FEEDBACK_QS_KEY
    );

    const isOfferVisible =
        history.location.pathname === PATH_OFFER &&
        (offerTab ?? OffersTablesTabsEnum.MANAGEMENT) === OffersTablesTabsEnum.MANAGEMENT &&
        hasSpecificIds(offerIds) &&
        offerFromMarketingFeedback === "1";

    const isSponsoredAdVisible =
        history.location.pathname === PATH_SPONSORED_ADS &&
        (sponsoredAdsTab ?? SponsoredAdsTablesTabsEnum.MANAGEMENT) ===
            SponsoredAdsTablesTabsEnum.MANAGEMENT &&
        hasSpecificIds(sponsoredAdIds) &&
        sponsoredAdFromMarketingFeedback === "1";

    const parseIds = (ids: string | string[] | null | undefined): number[] => {
        if (!ids) {
            return [];
        }
        const idArray = Array.isArray(ids) ? ids : [ids];
        return idArray
            .map((id) => {
                const parsed = parseInt(id, 10);
                return Number.isNaN(parsed) ? null : parsed;
            })
            .filter((id): id is number => id !== null);
    };

    return {
        isOfferVisible,
        isSponsoredAdVisible,
        ids: isOfferVisible ? parseIds(offerIds) : parseIds(sponsoredAdIds),
        isVisible: isOfferVisible || isSponsoredAdVisible,
    };
}

const ContextualBanner: FC<ContextualBannerProps> = ({ onBack, ids, entity, target }) => {
    const classes = useMarketingActionFeedbackBannerStyles();
    const { t } = useTranslation(
        `${MARKETING_ACTION_FEEDBACK_BANNER_COMPONENTS_NAMESPACE}:${MARKETING_ACTION_FEEDBACK_BANNER_TK}`
    );

    return (
        <Box sx={[classes.banner, classes.bannerProcessing]} role="status" aria-live="polite">
            <Box className="icon-wrapper" sx={classes.iconWrapper}>
                <InfoOutlined />
            </Box>
            <Box sx={classes.body}>
                <Box sx={classes.topRow}>
                    <Box sx={classes.textWrapper}>
                        <Typography variant="body">
                            {t("contextual-banner.title", {
                                entity,
                                count: ids.length,
                                context: target,
                            })}
                        </Typography>
                    </Box>
                    <Box sx={classes.actions}>
                        <Button variant="outlined" onClick={onBack} startIcon={<ClearIcon />}>
                            {t("contextual-banner.back-button")}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

const SingleBanner: FC<SingleBannerProps> = ({ operation, onClose, onErrorButtonClick }) => {
    const classes = useMarketingActionFeedbackBannerStyles();
    const { t } = useTranslation(
        `${MARKETING_ACTION_FEEDBACK_BANNER_COMPONENTS_NAMESPACE}:${MARKETING_ACTION_FEEDBACK_BANNER_TK}`
    );
    const { translateCommon: tCommon } = useTranslation();

    const status = getFeedbackStatus(operation);
    const StatusIcon = STATUS_ICON_MAP[status];
    const isBulkAction = operation.totalItems > 1;
    const isProcessing = status === "processing";
    const isClosable = !isProcessing;
    const subtitleText = getSubtitleText(operation, t, tCommon);
    const errorButtonLabel = getErrorButtonLabel(operation, status, t, tCommon);

    const statusSx = {
        processing: classes.bannerProcessing,
        success: classes.bannerSuccess,
        partial: classes.bannerPartial,
        error: classes.bannerError,
    }[status];

    return (
        <Box sx={[classes.banner, statusSx]} role="status" aria-live="polite">
            <Box
                className="icon-wrapper"
                sx={[classes.iconWrapper, isProcessing && classes.processingIcon]}
            >
                <StatusIcon />
            </Box>

            <Box sx={classes.body}>
                <Box sx={classes.topRow}>
                    <Box sx={classes.textWrapper}>
                        <Typography variant="subtitleMedium">
                            {t(`status.${status}.title`, {
                                count: isBulkAction ? 2 : 1,
                                context: operation.target,
                                entity: getEntityLabel(
                                    operation.target,
                                    { plural: isBulkAction },
                                    tCommon
                                ),
                            })}
                        </Typography>
                        {subtitleText?.map((line) => (
                            <Typography key={line}>{line}</Typography>
                        ))}
                    </Box>

                    {isProcessing && isBulkAction && (
                        <Typography sx={classes.counter}>
                            {t("counter", {
                                count: operation.processedItems,
                                context: operation.target,
                                processed: operation.processedItems,
                                total: operation.totalItems,
                                entity: getEntityLabel(
                                    operation.target,
                                    { plural: operation.processedItems > 1 },
                                    tCommon
                                ),
                            })}
                        </Typography>
                    )}

                    <Box sx={classes.actions}>
                        {errorButtonLabel && (
                            <Button
                                variant="outlined"
                                onClick={() => onErrorButtonClick(operation)}
                            >
                                {errorButtonLabel}
                            </Button>
                        )}
                        {isClosable && (
                            <IconButton
                                size="small"
                                onClick={() => onClose(operation.id)}
                                aria-label={tCommon("actions.close")}
                            >
                                <ClearIcon
                                    sx={{
                                        fontSize: 16,
                                        "& path": { color: "primary.main" },
                                    }}
                                />
                            </IconButton>
                        )}
                    </Box>
                </Box>

                {isProcessing && (
                    <LinearProgress
                        variant={isBulkAction ? "determinate" : "indeterminate"}
                        value={isBulkAction ? getProgressPercent(operation) : undefined}
                        sx={[classes.progressBar, { height: 6 }]}
                    />
                )}
            </Box>
        </Box>
    );
};

export const MarketingActionFeedbackBanner: FC = () => {
    const classes = useMarketingActionFeedbackBannerStyles();
    const history = useHistory();
    const { data: bulkOperations = [] } = useQueryUnseenBulkOperations();
    const { mutate: markSeen } = useMarkBulkOperationSeen();
    const contextualBanner = useContextualBannerVisibility();
    const { translateCommon: tCommon } = useTranslation();

    // Mark done operations as seen when navigating away
    useEffect(() => {
        const handleCleanDoneOperations = () => {
            bulkOperations
                .filter((operation) => operation.status === "done")
                .forEach((operation) => {
                    markSeen(operation.id);
                });
        };
        const unRegister = history.listen(handleCleanDoneOperations);
        window.addEventListener("beforeunload", handleCleanDoneOperations);
        return () => {
            unRegister();
            window.removeEventListener("beforeunload", handleCleanDoneOperations);
        };
    }, [bulkOperations, history, markSeen]);

    if (bulkOperations.length === 0 && !contextualBanner.isVisible) {
        return null;
    }

    const handleErrorButton = (operation: BulkOperation) => {
        if (operation.erroredItems.length === 0) {
            return;
        }
        markSeen(operation.id);
        history.push(getErrorNavigationUrl(operation));
    };

    const target = contextualBanner.isOfferVisible ? "offer" : "sponsoredAd";

    return (
        <Box sx={classes.container} id="marketing-action-feedback-container">
            {contextualBanner.isVisible && (
                <ContextualBanner
                    onBack={() =>
                        history.push(target === "offer" ? PATH_OFFER : PATH_SPONSORED_ADS)
                    }
                    target={target}
                    ids={contextualBanner.ids}
                    entity={getEntityLabel(
                        target,
                        { plural: contextualBanner.ids?.length > 1 },
                        tCommon
                    )}
                />
            )}
            {bulkOperations.map((operation) => (
                <SingleBanner
                    key={operation.id}
                    operation={operation}
                    onClose={markSeen}
                    onErrorButtonClick={handleErrorButton}
                />
            ))}
        </Box>
    );
};

//This component is separated from the main banner to allow better control of the Menu rendering in the stories
export const MarketingActionFeedbackBannerContent: FC<
    MarketingActionFeedbackBannerContentProps
> = ({ bulkOperations, onClose, onErrorButtonClick }) => {
    const classes = useMarketingActionFeedbackBannerStyles();

    return (
        <Box sx={classes.container} id="marketing-action-feedback-container">
            {bulkOperations.map((operation) => (
                <SingleBanner
                    key={operation.id}
                    operation={operation}
                    onClose={onClose}
                    onErrorButtonClick={onErrorButtonClick}
                />
            ))}
        </Box>
    );
};
